import { type ChangeEventHandler, type HTMLAttributes, type ReactNode, type Ref, useId } from 'react';
import { clsx } from 'clsx';
import { VerticalStack } from '../../alignment/vertical-stack';
import { ErrorMessage } from '../error-message/error-message';
import { InputHint } from '../input-hint/input-hint';
import { Label } from '../label/label';
import { useFocusWithin } from '../shared/use-focus-within';
import inputStyles from '../input.module.scss';
import styles from './text-area-input.module.scss';

export interface TextAreaInputProps extends HTMLAttributes<HTMLTextAreaElement> {
  disabled?: boolean;
  error?: string;
  hideLabel?: boolean;
  hint?: ReactNode;
  label: string;
  name: string;
  onChange?: ChangeEventHandler<HTMLTextAreaElement>;
  placeholder?: string;
  ref?: Ref<HTMLTextAreaElement>;
  required?: boolean;
  rows?: number;
  touched?: boolean;
  value: string;
}

export function TextAreaInput({
  className,
  error,
  hideLabel,
  hint,
  id: providedId,
  label,
  placeholder,
  ref,
  required,
  touched,
  value,
  ...rest
}: TextAreaInputProps) {
  const [hasFocus, handleFocusWithin, handleBlurWithin] = useFocusWithin();
  const generatedId = useId();
  const id = providedId || generatedId;
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  const showError = Boolean(touched && error && value);
  const showValue = Boolean(!hideLabel && (value || placeholder || hasFocus));

  return (
    <VerticalStack
      className={clsx(inputStyles.input, styles.textAreaInput, showValue && styles.hasValue)}
      onBlurCapture={handleBlurWithin}
      onFocusCapture={handleFocusWithin}
    >
      <Label
        className={styles.label}
        hasError={showError}
        htmlFor={id}
        isFloating={showValue}
        isHidden={hideLabel}
        label={label}
        required={required}
      />

      <div className={clsx(inputStyles.field, styles.fieldContainer, styles.inputElement)}>
        <textarea
          aria-describedby={hint ? hintId : undefined}
          aria-errormessage={showError ? errorId : undefined}
          aria-invalid={Boolean(error)}
          aria-required={required}
          className={clsx(styles.field, className)}
          id={id}
          placeholder={placeholder}
          required={required}
          value={value}
          {...rest}
          ref={ref}
        />
      </div>

      {hint ? (
        <InputHint className={styles.inputElement} id={hintId}>
          {hint}
        </InputHint>
      ) : null}
      {showError ? (
        <ErrorMessage className={styles.inputElement} id={errorId}>
          {error}
        </ErrorMessage>
      ) : null}
    </VerticalStack>
  );
}
